/**
 * Shared types and helpers for Gobodo server-side cache parameters.
 *
 * The Gobodo SDK's read endpoints accept an optional cache control bag:
 *   { cachekey, cacheduration, cachegroup, cachescope, invalidationkeys }
 * For object property PUTs, the backend also treats `cachekey` on the request as cache invalidation.
 *
 * Prior to this module, each call site declared the shape inline. New code
 * should import {@link GobodoCacheOpts} and use {@link makeCacheOpts} so the
 * shape stays consistent and cache-control opts are easy to locate.
 *
 * Domain-specific TTL constants (e.g. CASE_TRAVERSAL_CACHE_DURATION_SECONDS)
 * continue to live next to their callers — they encode intentional policy,
 * not boilerplate.
 */

/** Server-side cache options forwarded to Gobodo read endpoints. */
export interface GobodoCacheOpts {
  /** Cache key parts. Joined by the server; include all parameters that vary the response. */
  cachekey: string[];
  /** TTL in seconds. Omit to rely on Gobodo's default. */
  cacheduration?: string;
  /** Group(s) used for bulk invalidation via cache-group eviction. */
  cachegroup?: string;
  /** Scope name (e.g. per-user vs. shared). Rare. */
  cachescope?: string;
  /** Keys that should be invalidated alongside writes. */
  invalidationkeys?: string;
  /** Optional user cache partitioning. Rare. */
  cacheuser?: string;
}

/**
 * Baseline TTLs. Prefer a domain-specific constant (declared near its use)
 * when the value encodes real policy (e.g. "reports must refresh every X").
 * These defaults are for cases where no policy has been set yet.
 */
export const CACHE_TTL = {
  /** 60s — short-lived lists and counts (default). */
  SHORT: 60,
  /** 5min — layout metadata and schema. */
  MEDIUM: 300,
  /** 1h — rarely-changing reference data. */
  LONG: 3600,
  /** 24h — immutable or near-immutable data (e.g. record types). */
  DAY: 86400,
} as const;

/**
 * Build a cache-opts bag. Centralizes the inline `{ cachekey, cacheduration, ... }`
 * shape so touchpoints are easy to grep for.
 */
export function makeCacheOpts(
  key: string[],
  duration?: number | string,
  extras?: {
    group?: string[] | string;
    scope?: string;
    invalidate?: string[] | string;
    user?: string;
  }
): GobodoCacheOpts {
  const opts: GobodoCacheOpts = { cachekey: key };
  if (duration != null) opts.cacheduration = String(duration);
  if (extras?.group) opts.cachegroup = serializeGobodoCacheStringList(extras.group);
  if (extras?.scope) opts.cachescope = extras.scope;
  if (extras?.invalidate) opts.invalidationkeys = serializeGobodoCacheStringList(extras.invalidate);
  if (extras?.user) opts.cacheuser = extras.user;
  return opts;
}

function serializeGobodoCacheStringList(value: string[] | string): string {
  if (typeof value === 'string') return value;
  const filtered = value.map(v => String(v).trim()).filter(Boolean);
  if (filtered.length <= 1) return filtered[0] ?? '';
  return JSON.stringify(filtered);
}

/**
 * Server-side cache for `GraphObjectsApi.getObjectByKey(key, opts)`.
 * Single `cachekey` value = the Gobodo object key string (e.g. `001Nq00000DN9CoIAL__stg5_c`).
 * `graphId` is kept on the signature for call-site stability; it is not part of the key.
 */
export function makeObjectByKeyReadCacheOpts(key: string, _graphId: number): GobodoCacheOpts {
  return makeCacheOpts([key], CACHE_TTL.MEDIUM);
}

/**
 * Server-side cache for `GraphObjectsApi.getObjectById(id, opts)`.
 * Single `cachekey` value = the numeric id string. `graphId` is not part of the key.
 */
export function makeObjectByIdReadCacheOpts(id: number, _graphId: number): GobodoCacheOpts {
  return makeCacheOpts([String(id)], CACHE_TTL.MEDIUM);
}

/**
 * For `setObjectPropertiesById` / `setObjectPropertiesByKey`: pass `cachekey` on PUT so invalidation
 * matches {@link makeObjectByIdReadCacheOpts} (by id) / {@link makeObjectByKeyReadCacheOpts} (by key).
 */
export function makeObjectPropertyWriteCacheOpts(
  params:
    | { path: 'byId'; numericId: number; graphId: number }
    | { path: 'byKey'; recordKey: string; graphId: number }
): GobodoCacheOpts {
  if (params.path === 'byId') {
    return makeCacheOpts([String(params.numericId)], CACHE_TTL.MEDIUM);
  }
  return makeCacheOpts([params.recordKey], CACHE_TTL.MEDIUM);
}

/**
 * Server-side cache for CyberSweep audit template list (`getObjectsInGraphByType` for `Cyber_Sweep_Audit_Temp__c`).
 * Reference data; uses {@link CACHE_TTL.LONG} and a graph-scoped cache group for bulk invalidation if needed.
 */
export function makeAuditTemplateListReadCacheOpts(
  graphId: number,
  objectTypeKey: string,
  page: number,
  size: number
): GobodoCacheOpts {
  return makeCacheOpts(
    ['cybersweep', 'audit_templates', 'list', String(graphId), objectTypeKey, String(page), String(size)],
    CACHE_TTL.LONG,
    { group: [`gobodo-audit-templates-${graphId}`] }
  );
}

/** Single audit template by graph object key — longer TTL than generic {@link makeObjectByKeyReadCacheOpts}. */
export function makeAuditTemplateByKeyReadCacheOpts(key: string, graphId: number): GobodoCacheOpts {
  return makeCacheOpts(['cybersweep', 'audit_template', 'byKey', String(graphId), key], CACHE_TTL.LONG);
}

/**
 * Single audit template by numeric id (string id as returned from the app) — longer TTL than generic object-by-id cache.
 */
export function makeAuditTemplateByIdReadCacheOptsFromRecordId(recordId: string, graphId: number): GobodoCacheOpts {
  return makeCacheOpts(['cybersweep', 'audit_template', 'byId', String(graphId), String(recordId)], CACHE_TTL.LONG);
}

/**
 * Server-side cache for `GraphTraversalApi.traverseObjects`: CSAudit → audit template (one hop, OUTGOING).
 * Cache key must include `startObjectKey`, relationship type key, and end object type so it matches the request body.
 */
export function makeCyberSweepCsAuditToTemplateTraversalCacheOpts(
  graphId: number,
  startObjectKey: string,
  relationshipTypeKey: string,
  endObjectTypeKey: string
): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'traversal',
      'cs_audit_to_audit_template',
      String(graphId),
      startObjectKey,
      relationshipTypeKey,
      'OUTGOING',
      endObjectTypeKey,
      'maxPathLength',
      '1',
    ],
    CACHE_TTL.LONG,
    { group: [`gobodo-cybersweep-cs-audit-template-traversal-${graphId}`] }
  );
}
